import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { dismissAllToasts } from '@/lib/toast';
import { isTransientError } from '@/lib/transient';
import { broadcastAuthEvent } from '@/lib/authChannel';
import { loginPathFor } from '@/lib/loginRedirect';
import { login, logout, fetchSession, getAuthConfig } from './api';
import type { LoginInput } from './types';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  config: () => [...authKeys.all, 'config'] as const,
};

/**
 * Get current session/user
 */
export function useSession(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: authKeys.session(),
    queryFn: fetchSession,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Was `retry: false`. During an API deploy a single failed session check
    // left data undefined -> isAuthenticated false -> ProtectedRoute
    // history-replaced the user onto /login while their cookie was still
    // perfectly valid, and Back could not recover the page.
    retry: (failureCount, error) =>
      isTransientError(error) ? failureCount < 5 : false,
    enabled: options?.enabled,
  });
}

/**
 * Login mutation
 * On success: updates session cache and navigates to home
 * On error: handled by global error handler in queryClient
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: (data) => {
      // Update session cache with user data. NO navigate here: Login's
      // isAuthenticated effect is the ONLY post-login navigator — a second
      // navigate would move off /login first and strip ?redirectTo= before
      // the effect could read it.
      queryClient.setQueryData(authKeys.session(), data.user);
      // Clear any stale toasts
      dismissAllToasts();
      // The other tabs of this app follow (they re-ask the API who it is)
      broadcastAuthEvent({ type: 'signed-in' });
    },
    // Errors are handled by global mutationCache.onError in queryClient.ts
  });
}

/**
 * Logout mutation
 * Clears session and navigates to login
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      // Always clear all cached data and redirect, even if API call fails
      // This prevents stale user data from showing after re-login with a different account
      queryClient.clear();
      // The login URL remembers where the user logged out (?redirectTo=...)
      navigate(loginPathFor(window.location.pathname + window.location.search));
      // The other tabs of this app follow. Sent on settled, not success, so
      // they mirror exactly what this tab just did.
      broadcastAuthEvent({ type: 'signed-out', reason: 'logout' });
    },
  });
}

/**
 * Get auth configuration (password requirements, feature flags)
 */
export function useAuthConfig() {
  return useQuery({
    queryKey: authKeys.config(),
    queryFn: getAuthConfig,
    staleTime: 1000 * 60 * 60, // 1 hour - config rarely changes
  });
}
